import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { GOLD } from '../../theme/tokens';

const values = [
    { icon: 'bi-shield-check', title: 'Secure & Verifiable', text: 'Every badge is cryptographically signed and independently verifiable — employers and institutions can trust what they see.' },
    { icon: 'bi-palette', title: 'Fully Customizable', text: 'Design badges that reflect your brand, your criteria, and your community — with AI-powered assistance built in.' },
    { icon: 'bi-plug', title: 'Seamlessly Integrated', text: 'Works with your existing LMS, HR systems, and tools via our Open Badges-compliant API.' },
    { icon: 'bi-people', title: 'Open to Everyone', text: 'Free for educators, non-profits, and individuals — always. Premium features for organisations that need more.' },
];

export default function AboutPage() {
    return (
        <>
            {/* Hero */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10 }}>
                <Container maxWidth="lg">
                    <Grid container alignItems="center" spacing={4}>
                        <Grid item xs={12} lg={7}>
                            <Typography variant="overline" sx={{ color: GOLD, fontWeight: 700, letterSpacing: 2 }}>About TC Everywhere</Typography>
                            <Typography variant="h3" fontWeight={700} gutterBottom>Your Gateway to Digital Credentials</Typography>
                            <Typography variant="h6" sx={{ opacity: 0.85, mb: 4 }}>
                                TC Everywhere is a holistic solution that combines security, customization, user-friendliness,
                                seamless integration, and an innovative approach to badge earning.
                            </Typography>
                            <Button component={Link} to="/register" variant="contained" color="secondary" size="large" sx={{ mr: 2, fontWeight: 700 }}>
                                Get Started Free
                            </Button>
                            <Button component={Link} to="/how-it-works" variant="outlined" size="large" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>
                                How It Works
                            </Button>
                        </Grid>
                        <Grid item xs={12} lg={5} sx={{ textAlign: 'center' }}>
                            <Box component="i" className="bi bi-award-fill" sx={{ fontSize: '8rem', opacity: 0.2 }} />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Vision & Mission */}
            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', p: 2 }}>
                                <CardContent>
                                    <Box sx={{ bgcolor: 'primary.main', color: 'white', display: 'inline-flex', px: 2, py: 0.5, borderRadius: 1, mb: 2, fontSize: '0.875rem' }}>
                                        <Box component="i" className="bi bi-eye" sx={{ mr: 1 }} />Our Vision
                                    </Box>
                                    <Typography variant="h5" fontWeight={700} gutterBottom>Bridging the Gap in Digital Education</Typography>
                                    <Typography color="text.secondary">
                                        To bridge the gap in the digital education market and empower individuals to showcase
                                        their full skillsets and accomplishments in a way that matters.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', p: 2 }}>
                                <CardContent>
                                    <Box sx={{ bgcolor: GOLD, color: 'black', display: 'inline-flex', px: 2, py: 0.5, borderRadius: 1, mb: 2, fontSize: '0.875rem', fontWeight: 600 }}>
                                        <Box component="i" className="bi bi-bullseye" sx={{ mr: 1 }} />Our Mission
                                    </Box>
                                    <Typography variant="h5" fontWeight={700} gutterBottom>Credentials for Every Learner</Typography>
                                    <Typography color="text.secondary">
                                        To offer secure, verifiable, and visually appealing digital credentials that transform
                                        the way we recognize and celebrate learning achievements — universally accessible, for every learner.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Why We Built This */}
            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" fontWeight={700}>Why We Built TC Everywhere</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Digital credentials for a digital world.</Typography>
                    </Box>
                    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
                        <Typography color="text.secondary" paragraph>
                            From a collective standpoint, we recognized the evolving landscape of education and
                            professional development. As the world embraces online learning and remote work, the
                            need to accurately showcase one's skills and competencies digitally has never been
                            more critical. Traditional resumes and qualifications often fall short in capturing
                            the depth of an individual's capabilities.
                        </Typography>
                        <Typography color="text.secondary" paragraph>
                            That's why we built TC Everywhere — a dedicated venture to address this need. We believe
                            that digital badges and certificates are not just a representation of learning; they're
                            a dynamic portrayal of what you can do.
                        </Typography>
                        <Typography color="text.secondary">
                            With a strong foundation in the industry and a deep understanding of the digital education
                            ecosystem, we're well-positioned to make a difference in the lives of learners and institutions.
                            Join us in shaping the future of education, one digital credential at a time.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Values */}
            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} textAlign="center" mb={6}>What Sets Us Apart</Typography>
                    <Grid container spacing={4} textAlign="center">
                        {values.map(({ icon, title, text }) => (
                            <Grid item xs={12} sm={6} lg={3} key={title}>
                                <Box sx={{ p: 3 }}>
                                    <Box component="i" className={`bi ${icon}`} sx={{ fontSize: '2.5rem', color: 'primary.main' }} />
                                    <Typography variant="h6" fontWeight={700} mt={2} mb={1}>{title}</Typography>
                                    <Typography variant="body2" color="text.secondary">{text}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" fontWeight={700} gutterBottom>Ready to get started?</Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.85, mb: 4 }}>
                        Join thousands of issuers and earners building their digital credential story.
                    </Typography>
                    <Button component={Link} to="/register" variant="contained" color="secondary" size="large" sx={{ fontWeight: 700 }}>
                        Create Your Free Account
                    </Button>
                </Container>
            </Box>
        </>
    );
}
